import { EntitleError, type TErrorTexts } from "./entitleError";
import { mapErrorParams, type TErrorId } from "./mapApiErrorParams";
import { getI18nErrorTexts, getI18nTextIfExists } from "../i18n";

export default class ApiError extends EntitleError {
	public readonly errorId?: TErrorId;
	public readonly params?: Record<string, unknown>;
	public readonly statusCode?: number;

	constructor({
		errorId,
		params,
		title,
		message,
		statusCode,
		suppressError = false
	}: {
		errorId?: TErrorId;
		params?: Record<string, unknown>;
		title?: string;
		message?: string;
		statusCode?: number;
		suppressError?: boolean;
	}) {
		if (!message && !!errorId) {
			const i18nTexts = getI18nErrorTexts(errorId, mapErrorParams(errorId, params || {}));
			if (i18nTexts.message) {
				message = i18nTexts.message;
				title = i18nTexts.title || undefined;
			}
		}
		super(message, title, {}, suppressError);

		this.errorId = errorId;
		this.params = params;
		this.message = message || getI18nTextIfExists("error.unknown.message") || "";
		this.statusCode = statusCode;
	}

	override getTexts(): TErrorTexts {
		let i18nTexts: { title: string | null; message: string | null } = { title: null, message: null };
		if (this.errorId) {
			i18nTexts = getI18nErrorTexts(this.errorId, mapErrorParams(this.errorId, this.params || {}), !!this.message);
		}

		return {
			title: i18nTexts.title || this.title,
			message: i18nTexts.message || this.message
		};
	}

	override getActions() {
		if (this.errorId === "fetch.sessionExpired") {
			return {
				onRetry: () => window.location.reload(),
				retryText: getI18nTextIfExists("buttons.logIn"),
				closeText: null
			};
		}
		return super.getActions();
	}

	static from(
		{
			errorId,
			params,
			title,
			message
		}: {
			errorId?: TErrorId;
			params?: { [key: string]: string | number };
			title?: string;
			message?: string;
		},
		{ statusCode, suppressError }: { statusCode?: number; suppressError?: boolean } = {}
	) {
		if (!!title && !message) {
			message = title;
			title = undefined;
		}

		return new ApiError({
			errorId,
			params,
			message,
			title,
			statusCode,
			suppressError
		});
	}
}

export function getErrorTexts(error: ApiError | Error | null): TErrorTexts {
	if (!error)
		return {
			title: null,
			message: null
		};

	if (error instanceof ApiError) return error.getTexts();

	return {
		title: null,
		message: error.message
	};
}
